import React from 'react';
import { CompanyWallet } from '../../../services/api';
import { CopyButton } from '../../../utils/copyToClipboard';

interface CompanyWalletsTableProps {
  wallets: CompanyWallet[];
  loading?: boolean;
  onToggleActive?: (walletId: string) => void;
}

const CompanyWalletsTable: React.FC<CompanyWalletsTableProps> = ({
  wallets,
  loading = false,
  onToggleActive
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2777EF]"></div>
      </div>
    );
  }

  if (wallets.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400">No wallets found for this company.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-600">
        <thead className="bg-gray-700">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Wallet Address
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Value Amount
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Active
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Created
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-[#182233] divide-y divide-gray-600">
          {wallets.map((wallet) => (
            <tr key={wallet.id}>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center space-x-2">
                  <div className="text-sm font-medium text-white max-w-xs truncate">
                    {wallet.walletAddress.slice(0, 8)}...{wallet.walletAddress.slice(-8)}
                  </div>
                  <CopyButton text={wallet.walletAddress} />
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                {formatCurrency(wallet.valueAmount)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  wallet.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                  wallet.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                  wallet.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {wallet.status}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  wallet.status === 'ACTIVE' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {wallet.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                {new Date(wallet.createdAt).toLocaleDateString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                {wallet.status !== 'ACTIVE' && onToggleActive && (
                  <button
                    onClick={() => onToggleActive(wallet.id)}
                    className="text-blue-600 hover:text-blue-900 font-medium"
                  >
                    Enable
                  </button>
                )}
                {wallet.status === 'ACTIVE' && (
                  <span className="text-green-600 font-medium">Currently Active</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CompanyWalletsTable;
